import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, X, LogOut, User, LayoutDashboard, Store, ChevronDown } from "lucide-react";
import { useState } from "react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";

function flattenCats(nodes, depth = 0, out = []) {
  (nodes || []).forEach((c) => {
    out.push({ id: c.id, key: c.key, label: c.label, depth });
    flattenCats(c.children, depth + 1, out);
  });
  return out;
}

function CatTree({ nodes, onNav }) {
  const flat = flattenCats(nodes);
  if (flat.length === 0) return null;
  return (
    <ul className="space-y-1">
      {flat.map((c) => (
        <li key={c.id} style={{ paddingLeft: `${c.depth * 12}px` }}>
          <Link
            to={`/shop?category=${c.key}`}
            onClick={onNav}
            data-testid={`nav-cat-${c.key}`}
            className={`block py-1 text-sm transition-colors hover:text-[var(--brand-primary)] ${c.depth === 0 ? "font-semibold text-[#1A231C]" : "text-[#5C665D]"}`}
          >
            {c.depth > 0 ? "— " : ""}{c.label}
          </Link>
        </li>
      ))}
    </ul>
  );
}

export default function Navbar() {
  const { count } = useCart();
  const { user, logout } = useAuth();
  const { settings, navPages, categoryTree } = useSettings();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);

  const isStaff = user && (user.role === "admin" || user.role === "manager");

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const storeName = settings.store_name || "Victory General Store";

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-[#E8E4D9]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
            {settings.logo_url ? (
              <img src={settings.logo_url} alt={storeName} className="h-9 w-auto max-w-[160px] object-contain" />
            ) : (
              <>
                <span className="w-9 h-9 rounded-lg bg-[var(--brand-primary)] flex items-center justify-center">
                  <Store className="w-5 h-5 text-white" strokeWidth={1.5} />
                </span>
                <span className="font-display font-bold text-lg tracking-tight text-[#1A231C]">
                  {storeName}
                </span>
              </>
            )}
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            <Link to="/" data-testid="nav-link-home" className="text-[#5C665D] hover:text-[#1A231C] font-medium transition-colors">Home</Link>
            <div className="relative" onMouseEnter={() => setShopOpen(true)} onMouseLeave={() => setShopOpen(false)}>
              <Link to="/shop" data-testid="nav-link-shop" className="flex items-center gap-1 text-[#5C665D] hover:text-[#1A231C] font-medium transition-colors">
                Shop <ChevronDown className="w-4 h-4" />
              </Link>
              {shopOpen && categoryTree.length > 0 && (
                <div data-testid="nav-shop-menu" className="absolute left-0 top-full pt-3 w-[520px]">
                  <div className="bg-white border border-[#E8E4D9] rounded-xl shadow-[0_12px_40px_rgba(0,0,0,0.10)] p-5 grid grid-cols-2 gap-x-8 gap-y-3">
                    {categoryTree.map((root) => (
                      <div key={root.id}>
                        <CatTree nodes={[root]} onNav={() => setShopOpen(false)} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {navPages.map((p) => (
              <Link key={p.slug} to={`/page/${p.slug}`} data-testid={`nav-link-${p.slug}`} className="text-[#5C665D] hover:text-[#1A231C] font-medium transition-colors">
                {p.title}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/cart"
              data-testid="nav-cart-button"
              className="relative p-2 rounded-lg hover:bg-[#F4EFE6] transition-colors"
            >
              <ShoppingBag className="w-5 h-5 text-[#1A231C]" strokeWidth={1.5} />
              {count > 0 && (
                <span
                  data-testid="cart-count-badge"
                  className="absolute -top-1 -right-1 bg-[var(--brand-accent)] text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center"
                >
                  {count}
                </span>
              )}
            </Link>

            <div className="hidden md:flex items-center gap-2">
              {isStaff && (
                <Link
                  to="/admin"
                  data-testid="nav-admin-link"
                  className="flex items-center gap-1.5 text-sm font-medium text-[var(--brand-primary)] hover:text-[var(--brand-primary-hover)] px-3 py-2 rounded-lg hover:bg-[#F4EFE6] transition-colors"
                >
                  <LayoutDashboard className="w-4 h-4" strokeWidth={1.5} /> {user.role === "manager" ? "Manager" : "Admin"}
                </Link>
              )}
              {user ? (
                <>
                  {user.role === "customer" && (
                    <Link
                      to="/account"
                      data-testid="nav-account-link"
                      className="flex items-center gap-1.5 text-sm font-medium text-[#5C665D] hover:text-[#1A231C] px-3 py-2 rounded-lg hover:bg-[#F4EFE6] transition-colors"
                    >
                      <User className="w-4 h-4" strokeWidth={1.5} /> {user.name?.split(" ")[0]}
                    </Link>
                  )}
                  <button
                    onClick={handleLogout}
                    data-testid="nav-logout-button"
                    className="flex items-center gap-1.5 text-sm font-medium text-[#5C665D] hover:text-[var(--brand-accent)] px-3 py-2 rounded-lg hover:bg-[#F4EFE6] transition-colors"
                  >
                    <LogOut className="w-4 h-4" strokeWidth={1.5} /> Logout
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  data-testid="nav-login-link"
                  className="text-sm font-medium bg-[var(--brand-primary)] text-white px-4 py-2 rounded-lg hover:bg-[var(--brand-primary-hover)] transition-colors"
                >
                  Login
                </Link>
              )}
            </div>

            <button
              className="md:hidden p-2"
              onClick={() => setOpen(!open)}
              data-testid="nav-mobile-toggle"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#E8E4D9] bg-white px-4 py-4 space-y-2 max-h-[70vh] overflow-y-auto">
          <Link to="/" onClick={() => setOpen(false)} className="block py-2 text-[#1A231C] font-medium">Home</Link>
          <Link to="/shop" onClick={() => setOpen(false)} className="block py-2 text-[#1A231C] font-medium">Shop</Link>
          <div className="pl-3 border-l border-[#E8E4D9]">
            <CatTree nodes={categoryTree} onNav={() => setOpen(false)} />
          </div>
          {navPages.map((p) => (
            <Link key={p.slug} to={`/page/${p.slug}`} onClick={() => setOpen(false)} className="block py-2 text-[#1A231C] font-medium">
              {p.title}
            </Link>
          ))}
          {isStaff && (
            <Link to="/admin" onClick={() => setOpen(false)} className="block py-2 text-[var(--brand-primary)] font-medium">
              {user.role === "manager" ? "Manager Dashboard" : "Admin Dashboard"}
            </Link>
          )}
          {user ? (
            <button onClick={handleLogout} className="block py-2 text-[var(--brand-accent)] font-medium">
              Logout
            </button>
          ) : (
            <Link to="/login" onClick={() => setOpen(false)} className="block py-2 text-[var(--brand-primary)] font-medium">
              Login
            </Link>
          )}
        </div>
      )}
    </header>
  );
}
